import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PageSectionCard } from "../../../components/layout";
import { AppButton } from "../../../components/form";
import { platformAdminService, type PlatformStats } from "../../../services/platformAdminService";

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 600 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function DashboardPanel() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await platformAdminService.platformStats());
    } catch (e: any) {
      setError(e?.message || t("superAdmin.dashboard.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const support = stats?.activeSupportByPackage ?? {};
  const currency = stats?.currency || "EUR";
  const n = (v: number | undefined) => String(v ?? 0);

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <PageSectionCard
        title={t("superAdmin.dashboard.title")}
        description={t("superAdmin.dashboard.description")}
        actions={
          <AppButton variant="outlined" onClick={() => void load()} disabled={loading}>
            {t("common.refresh")}
          </AppButton>
        }
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label={t("superAdmin.dashboard.totalUsers")}
                value={n(stats?.totalUsers)}
                hint={t("superAdmin.dashboard.activeUsersHint", { count: stats?.activeUsers ?? 0 })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label={t("superAdmin.dashboard.workspaces")}
                value={n(stats?.totalWorkspaces)}
                hint={t("superAdmin.dashboard.workspacesHint", {
                  beas: stats?.beasWorkspaces ?? 0,
                  own: stats?.ownResourceWorkspaces ?? 0,
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label={t("superAdmin.dashboard.customQuotas")}
                value={n(stats?.customQuotaWorkspaces)}
                hint={t("superAdmin.dashboard.customQuotasHint")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label={t("superAdmin.dashboard.supportRevenue")}
                value={`${Number(stats?.supportRevenueThisMonth ?? 0).toFixed(2)} ${currency}`}
                hint={t("superAdmin.dashboard.supportRevenueHint")}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("superAdmin.dashboard.activeSupport")}
              </Typography>
              <Grid container spacing={1.5}>
                {(["ENTERPRISE", "TICKETS", "PRIORITY", "MEETINGS", "CUSTOM"] as const).map((key) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={key}>
                    <StatCard label={t(`superAdmin.dashboard.supportKey.${key}`)} value={n(support[key])} />
                  </Grid>
                ))}
              </Grid>
              {(stats?.supportExpiringIn30Days ?? 0) > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {t("superAdmin.dashboard.supportExpiring", { count: stats?.supportExpiringIn30Days ?? 0 })}
                </Alert>
              )}
            </Grid>
          </Grid>
        )}
      </PageSectionCard>
    </Stack>
  );
}
